import { ExtendedBitmapText, ExtendedContainer, gsap, SkySpine, soundService } from "@aceroll/core";

const ANIMATIONS = {
    SHOW: "appear",
    IDLE: "idle",
    ACTION: "action",
    HIDE: "out",
}
const ANIMATIONS_BASE = {
    SHOW: "appear",
    IDLE: "idle",
    ACTION: "action",
    HIDE: "out",
}
const SKINS = {
    BASE: "base",
    BONUS: "bonus",
    BONUS_ALTERNATIVE: "bonus_1",
}
const BITMAP_FONT = {
    ALIAS: 'font_1',
    ALIGN: 'center',
}

export default class MultiplierPanel extends ExtendedContainer {
    constructor(alias) {
        super('multiplier-panel');

        this._currentSkin = SKINS.BASE;
        this._currentMultiplierValue = 0;

        this._spine = this.addChild(new SkySpine(alias));
        this._spine.static(this._currentSkin, ANIMATIONS.HIDE);
        this.#createLabel();
        this.visible = false;
    }

    /////////////// PUBLIC /////////////////////////////////////////////////////////////////////////////////////////////

    async showBase(multiplier = 1) {
        if (this.visible) { return; }

        this.setBaseSkin();
        this.updateMultiplier(multiplier);
        return this.show();
    }

    async showBonus(multiplier = 1, alternative = false) {
        if (this.visible) { return; }

        alternative ? this.setBonusSkinAlternative() : this.setBonusSkin();
        this.updateMultiplier(multiplier);
        return this.show();
    }

    async updateMultiplier(newValue) {
        if (this._currentMultiplierValue === newValue || newValue < 0) return;

        this._currentMultiplierValue = newValue;
        this.#setMultiplierLabel();

        if (newValue > this._currentMultiplierValue && this.visible) {
            return this.playAction();
        }
    }

    async show() {
        this.visible = true;
        soundService.play('multiplier_tablau_appearing');
        await this._spine.setImmediately(this.#animationsList.SHOW);
        await this.#showLabel();

        this.playIdle();
    }

    async hide() {
        await this.#hideLabel();
        await this._spine.setImmediately(this.#animationsList.HIDE);

        this.#resetMultiplier();
        this.visible = false;
    }

    playIdle() {
        this._spine.addAnimation(this.#animationsList.IDLE, true);
    }

    async playAction() {
        soundService.play('multiplie_adding');
        return this._spine.setImmediately([this.#animationsList.ACTION, this.#animationsList.IDLE], true);
    }

    setBaseSkin() {
        this.#updateSkin(SKINS.BASE);
    }

    setBonusSkin() {
        this.#updateSkin(SKINS.BONUS);
    }

    setBonusSkinAlternative() {
        this.#updateSkin(SKINS.BONUS_ALTERNATIVE);
    }

    addText(value) {
        this._multiplierLabel.setText(value + 'X');
        this._multiplierLabel.pivot.set(this._multiplierLabel.width / 2, this._multiplierLabel.height / 2);
    }

    ////////////////////////////////////////////////////////////////////////////////////////////////////////////////////

    #createLabel() {
        this._multiplierLabelContainer = this.addChild(new ExtendedContainer('LabelContainer'));
        this._multiplierLabel = new ExtendedBitmapText('', BITMAP_FONT.ALIAS, {
            align: 'center',
            letterSpacing: -1
        });
        this._multiplierLabelContainer.addChild(this._multiplierLabel);
        this._multiplierLabelContainer.position.set(226, -54);
        this._multiplierLabelContainer.visible = false;
        this._multiplierLabelContainer.alpha = 0;
        this.#setMultiplierLabel();
    }

    async #showLabel(duration = 1) {
        return new Promise(resolve => {
            this._multiplierLabelContainer.visible = true;
            gsap.fromTo(this._multiplierLabelContainer,
                { alpha: 0 },
                { alpha: 1, duration, onComplete: () => resolve() }
            );
        });
    }

    async #hideLabel(duration = 0.5) {
        return new Promise(resolve => {
            gsap.fromTo(this._multiplierLabelContainer,
                { alpha: 1 },
                {
                    alpha: 0, duration, onComplete: () => {
                        this._multiplierLabelContainer.visible = false;
                        resolve();
                    }
                }
            );
        });
    }

    #setMultiplierLabel() {
        this._multiplierLabel.setText(this._currentMultiplierValue + 'X');
        this._multiplierLabel.pivot.set(this._multiplierLabel.width / 2, this._multiplierLabel.height / 2);
    }

    #updateSkin(newSkinName) {
        if (this._currentSkin !== newSkinName) {
            this._currentSkin = newSkinName;
            this._spine.setSkin(this._currentSkin);
        }
    }

    #resetMultiplier() {
        this.visible = false;
        this._spine.reset();
        this._currentMultiplierValue = 0;
    }

    get #animationsList() {
        return this.#isBaseView ? ANIMATIONS_BASE : ANIMATIONS;
    }

    get #isBaseView() {
        return this._currentSkin === SKINS.BASE;
    }
}